/**
 * ConfirmDialog — единая обёртка над <Modal> для действий с подтверждением.
 *
 * Заменяет window.confirm() в Files-модуле (Phase 1.6 / Phase 1.5,
 * Files UX Refactor 2026-08-06). window.confirm ненадёжен в iframe / при
 * tab blur / на быстром двойном клике (см. регрессию 2026-08-06 — иногда
 * файл удалялся без диалога).
 *
 * Гарантии:
 * - Inline-спиннер на confirm-кнопке во время `busy`.
 * - Кнопки disabled во время `busy` (anti-double-click race).
 * - confirmVariant: 'danger' (default, красная) или 'primary'.
 * - Reuses <Modal>: Esc, backdrop, body-lock, ARIA — out of the box.
 *
 * Использование:
 *   const [pendingId, setPendingId] = useState<string | null>(null);
 *   <ConfirmDialog
 *     open={pendingId !== null}
 *     title={t('files.upload.delete_confirm_title')}
 *     message={t('files.upload.delete_confirm', { name: ... })}
 *     busy={deleteMutation.isPending}
 *     onConfirm={() => { deleteMutation.mutate(pendingId!); setPendingId(null); }}
 *     onClose={() => setPendingId(null)}
 *   />
 */
import { Loader2 } from 'lucide-react';

import { Modal } from '@/components/ui/Modal';

export interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'danger' | 'primary';
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  confirmVariant = 'danger',
  busy = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const confirmClass =
    confirmVariant === 'danger'
      ? 'bg-danger-strong hover:bg-danger-hover text-white'
      : 'btn-primary';

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={title}
      maxWidth="sm"
      footer={
        <>
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            {cancelLabel ?? 'Cancel'}
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${confirmClass}`}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy && <Loader2 className="inline w-4 h-4 animate-spin mr-2" />}
            {confirmLabel ?? 'Confirm'}
          </button>
        </>
      }
    >
      <p className="text-sm text-surface-300 whitespace-pre-line">{message}</p>
    </Modal>
  );
}
